import React, { useState, useMemo } from 'react';
import { Check, ChevronRight, ChevronLeft, User, Phone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

// OptionButton component - Modern tasarım with gradient border
const OptionButton = ({ selected, onClick, children, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "w-full rounded-xl text-left transition-all duration-200",
      "hover:scale-[1.02] active:scale-[0.98]",
      selected 
        ? "p-[2px] bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 shadow-md" 
        : "p-[2px] bg-gray-200 hover:bg-gradient-to-r hover:from-pink-300 hover:via-rose-300 hover:to-pink-400"
    )}
  >
    <div className={cn(
      "w-full p-3 rounded-[10px] bg-white",
      selected && "bg-gradient-to-r from-pink-50 to-rose-50"
    )}>
      <div className="flex items-center justify-between">
        <span className={cn(
          "font-semibold text-sm",
          selected ? "text-pink-700" : "text-gray-700"
        )}>
          {children}
        </span>
        <div className={cn(
          "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          selected 
            ? "border-pink-500 bg-pink-500" 
            : "border-gray-300"
        )}>
          {selected && <Check className="w-3 h-3 text-white" />}
        </div>
      </div>
    </div>
  </button>
);

const QuoteWizard = ({ venue, onSubmit, isSubmitting }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    eventType: '',
    guestCount: '',
    catering: '',
    photography: '',
    eventMonth: '',
    fullName: '',
    phone: '',
    consentGiven: false
  });

  // Adımlar - Garson (Servis) adımı kaldırıldı
  const steps = [
    { id: 'eventType', title: 'Organizasyon' },
    { id: 'guestCount', title: 'Kişi Sayısı' },
    { id: 'catering', title: 'İkram' },
    { id: 'photography', title: 'Fotoğraf' },
    { id: 'eventMonth', title: 'Tarih' },
    { id: 'contact', title: 'İletişim' }
  ];

  // Organizasyon türleri
  const eventTypes = [
    { value: 'isteme-nisan', label: 'İsteme & Nişan' },
    { value: 'kina', label: 'Kına' },
    { value: 'dogum-gunu', label: 'Doğum Günü' },
    { value: 'bebek-mevlidi', label: 'Bebek Mevlidi' },
    { value: 'etkinlik', label: 'Etkinlik' }
  ];

  // Kişi sayıları - Mekanın min/max değerlerine göre dinamik (yuvarlak sayılarla)
  const guestCounts = useMemo(() => {
    const minCapacity = venue?.minCapacity || venue?.capacity?.min;
    const maxCapacity = venue?.maxCapacity || venue?.capacity?.max;
    
    // Eğer mekan kapasite bilgisi girmemişse varsayılan seçenekleri göster
    if (!minCapacity || !maxCapacity) {
      return [
        { value: '50-60', label: '50-60 Kişi' },
        { value: '60-70', label: '60-70 Kişi' },
        { value: '70-80', label: '70-80 Kişi' },
        { value: '80-100', label: '80-100 Kişi' },
        { value: '100-150', label: '100-150 Kişi' },
        { value: '150+', label: '150+ Kişi' }
      ];
    }
    
    const min = parseInt(minCapacity);
    const max = parseInt(maxCapacity);
    const totalRange = max - min;
    
    // 10'un katına yuvarla (aşağı)
    const roundDown = (n) => Math.floor(n / 10) * 10;
    // 10'un katına yuvarla (yukarı)
    const roundUp = (n) => Math.ceil(n / 10) * 10;
    
    const ranges = [];
    
    // Küçük aralık (50 ve altı) - 2 seçenek
    if (totalRange <= 50) {
      const mid = roundDown(min + totalRange / 2);
      ranges.push({ value: `${min}-${mid}`, label: `${min}-${mid} Kişi` });
      ranges.push({ value: `${mid}-${max}`, label: `${mid}-${max} Kişi` });
    }
    // Orta aralık (51-100) - 3 seçenek
    else if (totalRange <= 100) {
      const third = Math.round(totalRange / 3);
      const point1 = roundDown(min + third);
      const point2 = roundDown(min + third * 2);
      ranges.push({ value: `${min}-${point1}`, label: `${min}-${point1} Kişi` });
      ranges.push({ value: `${point1}-${point2}`, label: `${point1}-${point2} Kişi` });
      ranges.push({ value: `${point2}-${max}`, label: `${point2}-${max} Kişi` });
    }
    // Büyük aralık (100+) - 4 seçenek
    else {
      const quarter = Math.round(totalRange / 4);
      const point1 = roundDown(min + quarter);
      const point2 = roundDown(min + quarter * 2);
      const point3 = roundDown(min + quarter * 3);
      ranges.push({ value: `${min}-${point1}`, label: `${min}-${point1} Kişi` });
      ranges.push({ value: `${point1}-${point2}`, label: `${point1}-${point2} Kişi` });
      ranges.push({ value: `${point2}-${point3}`, label: `${point2}-${point3} Kişi` });
      ranges.push({ value: `${point3}-${max}`, label: `${point3}-${max} Kişi` });
    }
    
    return ranges;
  }, [venue]);

  // İkram seçenekleri
  const cateringOptions = [
    { value: 'kendim', label: 'İkramlıkları kendim getireceğim' },
    { value: 'ikramli', label: 'İkramlı İstiyorum' }
  ];

  // Fotoğraf/Video seçenekleri
  const photographyOptions = [
    { value: 'yok', label: 'İstemiyorum' },
    { value: 'fotograf', label: 'Fotoğraf' },
    { value: 'hikaye', label: 'Hikaye' },
    { value: 'fotograf-hikaye', label: 'Fotoğraf + Hikaye' },
    { value: 'fotograf-video', label: 'Fotoğraf + Video' },
    { value: 'video', label: 'Video' }
  ];

  // Ay seçenekleri (şu an + 6 ay)
  const monthOptions = useMemo(() => {
    const months = [];
    const now = new Date();
    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    for (let i = 0; i <= 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      months.push({
        value: `${date.getMonth() + 1}-${year}`,
        label: `${month} ${year}`
      });
    }
    return months;
  }, []);

  const handleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Seçim yapıldıktan sonra otomatik olarak sonraki adıma geç
    // Son adım (iletişim bilgileri) hariç
    if (currentStep < steps.length - 1) {
      // Küçük bir gecikme ile geçiş yap (görsel geri bildirim için)
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 200);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.eventType !== '';
      case 1: return formData.guestCount !== '';
      case 2: return formData.catering !== '';
      case 3: return formData.photography !== '';
      case 4: return formData.eventMonth !== '';
      case 5: return formData.fullName.trim() !== '' && formData.phone.trim() !== '' && formData.consentGiven;
      default: return false;
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1 && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (canProceed()) {
      onSubmit(formData);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Organizasyon Türü
        return (
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800 mb-3">
              Ne tür bir organizasyon düşünüyorsunuz?
            </p>
            <div className="grid gap-2">
              {eventTypes.map(type => (
                <OptionButton
                  key={type.value}
                  selected={formData.eventType === type.value}
                  onClick={() => handleSelect('eventType', type.value)}
                >
                  {type.label}
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 1: // Kişi Sayısı
        return (
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800 mb-3">
              Kaç kişilik organizasyon?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {guestCounts.map(count => (
                <OptionButton
                  key={count.value}
                  selected={formData.guestCount === count.value}
                  onClick={() => handleSelect('guestCount', count.value)}
                >
                  {count.label}
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 2: // İkram
        return (
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800 mb-3">
              İkram tercihiniz?
            </p>
            <div className="grid gap-2">
              {cateringOptions.map(option => (
                <OptionButton
                  key={option.value}
                  selected={formData.catering === option.value}
                  onClick={() => handleSelect('catering', option.value)}
                >
                  {option.label}
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 3: // Fotoğraf/Video
        return (
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800 mb-3">
              Fotoğraf/Video hizmeti?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {photographyOptions.map(option => (
                <OptionButton
                  key={option.value}
                  selected={formData.photography === option.value}
                  onClick={() => handleSelect('photography', option.value)}
                >
                  {option.label}
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 4: // Tahmini Tarih
        return (
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800 mb-3">
              Tahmini organizasyon tarihi?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {monthOptions.map(month => (
                <OptionButton
                  key={month.value}
                  selected={formData.eventMonth === month.value}
                  onClick={() => handleSelect('eventMonth', month.value)}
                >
                  {month.label}
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 5: // İletişim Bilgileri
        return (
          <div className="space-y-3">
            <p className="text-sm font-bold text-gray-800 mb-3">
              İletişim bilgileriniz
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Ad Soyad *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Adınız ve soyadınız"
                    className="pl-9 h-10 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Telefon *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="05XX XXX XX XX"
                    className="pl-9 h-10 text-sm"
                  />
                </div>
              </div>
              
              {/* KVKK Onay Checkbox */}
              <div className="pt-2">
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.consentGiven}
                    onChange={(e) => setFormData({...formData, consentGiven: e.target.checked})}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                  />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    Kişisel bilgilerimin <strong>{venue?.name}</strong> ile paylaşılmasını kabul ediyorum.
                  </span>
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="relative rounded-2xl shadow-xl p-[3px] animate-gradient-shift" style={{
      background: 'linear-gradient(90deg, #ec4899, #a855f7, #6366f1, #a855f7, #ec4899)',
      backgroundSize: '200% 100%'
    }}>
      {/* Content Container */}
      <div className="relative bg-white rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <h2 className="text-lg font-bold text-gray-800">Hemen Teklif Al</h2>
          </div>
          <p className="text-gray-500 text-sm font-medium">{venue?.name}</p>
        </div>

        {/* Progress Bar - Modern */}
        <div className="px-4 pt-4 pb-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div 
                className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 shadow-sm",
                  index < currentStep 
                    ? "bg-gradient-to-br from-green-400 to-green-500 text-white scale-100" 
                    : index === currentStep 
                      ? "bg-gradient-to-br from-pink-500 to-rose-500 text-white scale-110 ring-4 ring-pink-100" 
                      : "bg-white text-gray-400 border-2 border-gray-200"
                )}
              >
                {index < currentStep ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div 
                  className={cn(
                    "flex-1 h-1 mx-1.5 rounded-full transition-all duration-300",
                    index < currentStep ? "bg-gradient-to-r from-green-400 to-green-500" : "bg-gray-200"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="text-center text-xs font-medium text-gray-600">
          <span className="text-pink-600">{currentStep + 1}</span>/{steps.length} • {steps[currentStep].title}
        </p>
      </div>

      {/* Content */}
      <div className="p-4 min-h-[220px]">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="px-4 pb-4 flex gap-3 bg-gray-50 border-t border-gray-100 pt-3">
        {currentStep > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={prevStep}
            className="flex-1 h-11 rounded-xl border-2 hover:bg-gray-100 font-medium"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Geri
          </Button>
        )}
        
        {/* Son adımda (iletişim bilgileri) Teklif Al butonu göster */}
        {currentStep === steps.length - 1 && (
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!canProceed() || isSubmitting}
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold shadow-lg shadow-green-500/30 transition-all hover:shadow-xl hover:shadow-green-500/40"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gönderiliyor...
              </>
            ) : (
              'Teklif Al'
            )}
          </Button>
        )}
      </div>
      </div>
    </div>
  );
};

export default QuoteWizard;
